import type { FC, ReactElement, ReactNode } from "react"

import { motion, useReducedMotion } from "framer-motion"
import isNull from "lodash-es/isNull"
import { type NavigateFunction, Outlet, useNavigate } from "react-router-dom"

import { ArrowIcon } from "@/3514/assets"
import { Button, CoachHeader, ETypographyColor, ETypographyTag, ETypographyWeight, Typography } from "@/3514/components"
import {
    type TScreenBreakpoints,
    useDefaultStylesReset,
    useHtmlScrollbarGutter,
    useScreenBreakpoints
} from "@/3514/hooks"
import { type IAppState, useAppSelector } from "@/3514/store/app"
import { ERoundDirection, buildCssClassName, getTransitionTwClasses, withPressHandler } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

const MAIN_CLASS_NAME: string = "c-layout"

const classes = {
    holder: (): string => "[min-height:100dvh] flex flex-col flex-1",
    backButton: (): string =>
        buildCssClassName(
            "flex",
            "w-fit",
            "items-center",
            "mx-[30px]",
            "mt-[25px]",
            "cursor-pointer",
            "gap-x-[5px]",
            "border-b-[1px]",
            "border-transparent",
            "hover:border-accent",
            "focus:border-accent",
            getTransitionTwClasses("all", 100)
        ),
    container: ({ withBackButton }: { withBackButton: boolean }): string =>
        buildCssClassName(
            withBackButton ? "flex w-auto min-h-[calc(100%-130px)]" : "flex flex-col flex-1 w-auto h-[calc(100%-75px)]"
        ),
    main: (): string => "w-full flex flex-col flex-1"
} as const

type TProps = {
    withBackButton?: boolean
    children?: ReactNode
    withHtmlScrollBarGutter?: boolean
}
const CoachLayout: FC<TProps> = ({
    withBackButton = false,
    children = null,
    withHtmlScrollBarGutter = false
}: TProps): ReactElement => {
    useDefaultStylesReset(MAIN_CLASS_NAME)

    useHtmlScrollbarGutter({ isEnabled: withHtmlScrollBarGutter })

    const { isOpenSidebar }: IAppState = useAppSelector()

    const { t }: IUseTranslation = useTranslation()

    const navigate: NavigateFunction = useNavigate()

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const isMotionReduced: boolean = useReducedMotion()

    const backAction: ReturnType<typeof withPressHandler> = withPressHandler(async (): Promise<void> => navigate(-1))

    return (
        <motion.div
            className={classes.holder()}
            animate={{
                marginLeft: breakpoints.xs || breakpoints.sm ? 0 : isOpenSidebar ? 250 : 80
            }}
            transition={{
                duration: isMotionReduced || Boolean(breakpoints.xs || breakpoints.sm) ? 0 : 0.4,
                type: "tween",
                ease: "easeInOut"
            }}
        >
            <CoachHeader />

            {withBackButton && (
                <Button
                    tabIndex={-1}
                    isContainer
                    className={classes.backButton()}
                    onClick={backAction}
                    onKeyDown={backAction}
                >
                    <ArrowIcon direction={ERoundDirection.Left} />

                    <Typography
                        text={t("common.back")}
                        color={ETypographyColor.Blue200}
                        weight={ETypographyWeight.Bold}
                        tag={ETypographyTag.Span}
                    />
                </Button>
            )}

            <div className={classes.container({ withBackButton })}>
                <main className={buildCssClassName(classes.main(), MAIN_CLASS_NAME)}>
                    {isNull(children) ? <Outlet /> : children}
                </main>
            </div>
        </motion.div>
    )
}

CoachLayout.displayName = "CoachLayout"

export { CoachLayout }
